import { motion } from "framer-motion";
import {
  Globe, HelpCircle, Shield, Lightbulb, Compass,
  CheckSquare, Handshake, Clock, Pin, X, ThumbsUp,
  ExternalLink, BookOpen
} from "lucide-react";

const CARD_TYPES = {
  fact_check:  { icon: Globe,       label: "Fact Check",  dot: "bg-blue-500",    accent: "border-l-blue-400" },
  follow_up:   { icon: HelpCircle,  label: "Follow Up",   dot: "bg-violet-500",  accent: "border-l-violet-400" },
  defense:     { icon: Shield,      label: "Defense",      dot: "bg-amber-500",   accent: "border-l-amber-400" },
  example:     { icon: Lightbulb,   label: "Example",      dot: "bg-emerald-500", accent: "border-l-emerald-400" },
  nudge:       { icon: Compass,     label: "Agenda",       dot: "bg-red-500",     accent: "border-l-red-400" },
  action_item: { icon: CheckSquare, label: "Action Item",  dot: "bg-emerald-600", accent: "border-l-emerald-500" },
  objection:   { icon: Handshake,   label: "Negotiation",  dot: "bg-orange-500",  accent: "border-l-orange-400" },
  // pace_alert:  { icon: Clock,       label: "Pace",         dot: "bg-red-500",     accent: "border-l-red-400" },
  grounded:    { icon: BookOpen,    label: "Grounded",     dot: "bg-teal-500",    accent: "border-l-teal-400" },
};

const VERDICT_LABEL = {
  accurate:           { text: "Accurate",   className: "text-emerald-600" },
  inaccurate:         { text: "Inaccurate", className: "text-red-600" },
  partially_accurate: { text: "Partial",    className: "text-amber-600" },
  answer:             { text: "Answer",     className: "text-blue-600" },
};

function extractCardData(card) {
  const { card_type, content, urgency } = card;
  let response = null;
  let context = [];
  let sourceUrl = null;
  let verdict = null;
  let progress = null;

  switch (card_type) {
    case "fact_check":
      verdict = VERDICT_LABEL[content.verdict] || VERDICT_LABEL.partially_accurate;
      response = content.explanation;
      if (content.claim) context.push(`"${content.claim}"`);
      if (content.sources?.length > 0) sourceUrl = content.sources[0];
      break;

    case "follow_up":
      response = content.question;
      if (content.rationale) context.push(content.rationale);
      else if (content.trigger) context.push(`Re: "${content.trigger}"`);
      break;

    case "defense":
      response = content.reframe || content.counter_points?.[0];
      if (content.challenge) context.push(`"${content.challenge}"`);
      if (content.counter_points?.length > 1) {
        context.push(content.counter_points.slice(1).join(" · "));
      }
      break;

    case "example":
      response = content.examples?.[0]?.example;
      if (content.concept) context.push(content.concept);
      if (content.examples?.[0]?.how_to_use) context.push(content.examples[0].how_to_use);
      break;

    case "nudge": {
      response = content.transition || content.uncovered_item;
      const nudgeParts = [];
      if (content.uncovered_item && content.transition) nudgeParts.push(content.uncovered_item);
      if (content.reason) nudgeParts.push(content.reason);
      if (nudgeParts.length) context.push(nudgeParts.join(" · "));
      break;
    }

    case "action_item":
      response = content.quote || `So just to confirm, ${content.owner || "they"} will have this by ${content.deadline || "the deadline"}?`;
      if (content.action) context.push(content.action);
      const meta = [content.owner, content.deadline].filter(Boolean);
      if (meta.length) context.push(meta.join(" · "));
      break;

    case "objection":
      response = content.suggested_response || content.compromise;
      if (content.tension_source) context.push(content.tension_source);
      if (content.their_concern) context.push(content.their_concern);
      if (content.compromise && content.suggested_response) {
        context.push(`Alt: "${content.compromise}"`);
      }
      break;

    // case "pace_alert": {
    //   const pct = content.total_allocated_minutes > 0
    //     ? Math.round((content.elapsed_minutes / content.total_allocated_minutes) * 100)
    //     : 0;
    //   response = content.uncovered_items?.length > 0
    //     ? `Let me wrap this — we can park ${content.uncovered_items[0]} for next time.`
    //     : "Let me wrap this point so we stay on time.";
    //   if (content.message) context.push(content.message);
    //   if (content.uncovered_items?.length > 1) {
    //     context.push(`Still: ${content.uncovered_items.slice(1).join(", ")}`);
    //   }
    //   progress = Math.min(pct, 100);
    //   break;
    // }

    case "grounded":
      response = content.suggestion;
      if (content.evidence) context.push(content.evidence);
      if (content.trigger) context.push(`Re: "${content.trigger}"`);
      break;

    default:
      break;
  }

  return { response, context, sourceUrl, verdict, progress, sourceChip: card.card_type === "grounded" ? content?.source_title : null };
}

function formatCardTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

export default function SmartCard({ card, onDismiss, onPin, onUsed, readOnly }) {
  const config = CARD_TYPES[card.card_type] || CARD_TYPES.follow_up;
  const Icon = config.icon;
  const { response, context, sourceUrl, verdict, progress, sourceChip } = extractCardData(card);
  const isImmediate = card.urgency === "immediate";
  const timeLabel = formatCardTime(card.timestamp);

  if (!response) return null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 60, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 420, damping: 32 }}
      className={`
        group bg-white rounded border border-border border-l-[3px] ${config.accent}
        px-6 py-5 transition-shadow duration-150
        ${isImmediate
          ? "shadow-[0_2px_20px_-2px_rgba(0,0,0,0.08)] border-foreground/[0.08]"
          : "shadow-sm hover:shadow-[0_2px_20px_-2px_rgba(0,0,0,0.06)]"
        }
      `}
    >
      {/* Row 1: Type + Urgency + Actions */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${config.dot}`} />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {config.label}
          </span>
          {verdict && (
            <>
              <span className="text-muted-foreground/25">·</span>
              <span className={`text-xs font-semibold ${verdict.className}`}>
                {verdict.text}
              </span>
            </>
          )}
          {!verdict && isImmediate && (
            <>
              <span className="text-muted-foreground/25">·</span>
              <span className="text-xs font-semibold text-foreground/60">Now</span>
            </>
          )}
          {timeLabel && (
            <>
              <span className="text-muted-foreground/25">·</span>
              <span className="text-[11px] tabular-nums text-muted-foreground/40">{timeLabel}</span>
            </>
          )}
        </div>

        {!readOnly && (
          <div className="flex items-center shrink-0">
            <button
              onClick={() => onUsed?.(card.card_id)}
              className="p-1.5 rounded-md text-muted-foreground/40 hover:text-emerald-600 transition-colors"
              title="Useful"
            >
              <ThumbsUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onPin?.(card.card_id)}
              className={`p-1.5 rounded-md transition-colors ${
                card.pinned
                  ? "text-amber-500 !opacity-100"
                  : "text-muted-foreground/40 hover:text-amber-500"
              }`}
              title={card.pinned ? "Unpin" : "Pin"}
            >
              <Pin className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDismiss?.(card.card_id)}
              className="p-1.5 rounded-md text-muted-foreground/40 hover:text-foreground transition-colors"
              title="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

      </div>

      {/* Row 2: Response — the hero */}
      <p className={`text-xl leading-[1.45] tracking-[-0.01em] text-foreground ${isImmediate ? "font-medium" : ""}`}>
        "{response}"
      </p>

      {/* Row 3: Context + Source */}
      {(context.length > 0 || sourceUrl || progress != null) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
          {context.length > 0 && (
            <p className="text-[13px] text-foreground/55 leading-snug">
              {context.join(" · ")}
            </p>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[13px] text-blue-500 hover:text-blue-700 transition-colors shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-3 h-3" />
              Source
            </a>
          )}
        </div>
      )}

      {/* Source chip for grounded cards */}
      {sourceChip && (
        <div className="mt-2.5 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 font-medium">
            <BookOpen className="w-3 h-3" />
            from: {sourceChip}
          </span>
        </div>
      )}

      {/* Progress bar for pace alerts */}
      {progress != null && (
        <div className="mt-3 w-full h-1.5 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-red-400 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </motion.div>
  );
}
